import { Extension, type Editor } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection, type EditorState } from "@tiptap/pm/state";
import { LEAF, parseSlashTrigger, planSlashConsume } from "@/components/editor/slashModel";

/**
 * Watches the text before the cursor for the slash menu's trigger, and
 * carries out Olivia's rule for it (27 Sep 2026, §4): a command consumes the
 * `/` and its filter text; Esc leaves them.
 *
 * It only reports; the menu decides what to show. The rules for what counts
 * as a trigger, and what to delete, live in `slashModel`, where they are
 * tested. This is the one place the typed text is removed, whichever command
 * runs — block rows, Lorem, Paste LaTeX… alike.
 */

/** Where the block a command inserts belongs, once its trigger is gone. */
export interface SlashTarget {
  /** Start of the paragraph the trigger was in. Mapped through edits. */
  pos: number;
  /** "replace": the line was only the trigger. "after": it had other text. */
  mode: "replace" | "after";
}

export interface SlashTriggerState {
  active: boolean;
  /** Filter text after the `/`. */
  query: string;
  /** Document range the trigger occupies, `/` included. */
  from: number;
  to: number;
  /**
   * Where a trigger the author dismissed with Esc begins. While they keep
   * typing that same trigger the menu stays shut — Esc is how a literal `/`
   * is written, so the next letter must not reopen it.
   */
  dismissedFrom: number | null;
  target: SlashTarget | null;
}

export const SLASH_INACTIVE: SlashTriggerState = {
  active: false, query: "", from: 0, to: 0, dismissedFrom: null, target: null,
};

type Meta =
  | { type: "dismiss" }
  | { type: "consume"; target: SlashTarget }
  | { type: "take" };

export const slashTriggerPluginKey = new PluginKey<SlashTriggerState>("slashTrigger");

export const SlashTrigger = Extension.create({
  name: "slashTrigger",

  addProseMirrorPlugins() {
    return [
      new Plugin<SlashTriggerState>({
        key: slashTriggerPluginKey,
        state: {
          init: () => SLASH_INACTIVE,
          apply(tr, prev) {
            const meta = tr.getMeta(slashTriggerPluginKey) as Meta | undefined;

            // Positions survive edits made elsewhere; a deleted `/` or
            // paragraph forgets them.
            let target = prev.target;
            let dismissedFrom = prev.dismissedFrom;
            if (tr.docChanged) {
              if (target) {
                const r = tr.mapping.mapResult(target.pos, 1);
                target = r.deletedAfter ? null : { ...target, pos: r.pos };
              }
              if (dismissedFrom !== null) {
                const r = tr.mapping.mapResult(dismissedFrom, 1);
                dismissedFrom = r.deletedAfter ? null : r.pos;
              }
            }

            if (meta?.type === "dismiss") {
              return { ...SLASH_INACTIVE, dismissedFrom: prev.active ? prev.from : dismissedFrom, target };
            }
            if (meta?.type === "consume") {
              return { ...SLASH_INACTIVE, target: meta.target };
            }
            if (meta?.type === "take") target = null;

            const inactive = { ...SLASH_INACTIVE, dismissedFrom, target };
            // A caret move alone never opens the menu (typing does); it only
            // re-reads an open one, which closes when the caret leaves it.
            if (!tr.docChanged && !(tr.selectionSet && prev.active)) {
              return meta?.type === "take" ? { ...prev, target } : prev;
            }

            const { selection } = tr;
            if (!selection.empty) return inactive;
            const { $from } = selection;
            if ($from.parent.type.name !== "paragraph") return inactive;

            // Leaves (a citation, inline math) read as U+FFFC, so a trigger
            // never reaches back across one.
            const textBefore = $from.parent.textBetween(0, $from.parentOffset, undefined, LEAF);
            const trigger = parseSlashTrigger(textBefore);
            if (!trigger) return inactive;

            const from = $from.pos - trigger.length;
            if (from === dismissedFrom) return inactive;

            return { active: true, query: trigger.query, from, to: $from.pos, dismissedFrom: null, target };
          },
        },
      }),
    ];
  },
});

export function readSlashTrigger(state: EditorState): SlashTriggerState {
  return slashTriggerPluginKey.getState(state) ?? SLASH_INACTIVE;
}

/** Esc: close the menu on this trigger and leave the text as typed. */
export function dismissSlashTrigger(editor: Editor): void {
  if (!readSlashTrigger(editor.state).active) return;
  editor.view.dispatch(editor.state.tr.setMeta(slashTriggerPluginKey, { type: "dismiss" } satisfies Meta));
}

/**
 * Remove what the author typed to reach a command — `/` and the filter text —
 * and remember where the command's block belongs. Runs before the command,
 * so a dialog it opens finds the document already clean. Returns the query
 * that was consumed (Lorem reads its count from it), or null when no menu was
 * open.
 */
export function consumeSlashTrigger(editor: Editor): string | null {
  const s = readSlashTrigger(editor.state);
  if (!s.active) return null;
  const { state } = editor;
  const $at = state.doc.resolve(s.from);
  const line = $at.parent;
  if (line.type.name !== "paragraph") return null;

  const start = $at.start();
  const text = line.textBetween(0, line.content.size, undefined, LEAF);
  const plan = planSlashConsume(text, s.from - start, s.to - start);
  const tr = state.tr.delete(start + plan.from, start + plan.to);
  tr.setSelection(TextSelection.create(tr.doc, start + plan.from));
  tr.setMeta(slashTriggerPluginKey, {
    type: "consume",
    target: { pos: $at.before(), mode: plan.emptyLine ? "replace" : "after" },
  } satisfies Meta);
  editor.view.dispatch(tr);
  return s.query;
}

/**
 * The range a slash command's block goes into, used once: the emptied
 * paragraph itself (replace it), or the point after the line's block. Null
 * when no command is pending, or the author has since moved elsewhere — the
 * caller then inserts where it always does.
 */
export function takeSlashInsertRange(editor: Editor): { from: number; to: number } | null {
  const t = readSlashTrigger(editor.state).target;
  if (!t) return null;
  editor.view.dispatch(editor.state.tr.setMeta(slashTriggerPluginKey, { type: "take" } satisfies Meta));

  const { doc, selection } = editor.state;
  const node = t.pos < doc.content.size ? doc.nodeAt(t.pos) : null;
  if (!node || node.type.name !== "paragraph") return null;
  const end = t.pos + node.nodeSize;
  if (selection.from < t.pos || selection.from > end) return null;
  if (t.mode === "replace" && node.content.size === 0) return { from: t.pos, to: end };
  const after = doc.resolve(t.pos + 1).after(1);
  return { from: after, to: after };
}

/** Just after the top-level block holding the cursor; the document's end when there is none. */
export function afterCurrentBlock(editor: Editor): number {
  const { selection, doc } = editor.state;
  if (selection.$from.depth >= 1) {
    try {
      return selection.$from.after(1);
    } catch {
      // $from.after can throw at boundary positions — fall back.
    }
  }
  return doc.content.size;
}
